'use client';

import { Chevron, Slider } from '@sprd/sprd-component-kit/v2';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import {
  GalleryImage,
  ResponsiveImageDataWithBadges,
} from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/components/GalleryImage';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { CSSPropertiesWithVars } from '@/utility/imageGalleryHelpers';

type ImageGalleryProps = {
  images: ResponsiveImageDataWithBadges[];
  exchangeFirstImageOnMobile?: boolean;
  spacingFactor?: number;
  bottomOffsetDuration?: number;
  imageAltText?: string;
};

const MAIN_IMAGE_CLASSES = 'aspect-3/4 w-full bg-[#F2F2F2] object-contain!';
const THUMB_IMAGE_CLASSES = 'aspect-3/4 bg-[#F2F2F2] object-contain!';
const THUMB_SCROLL_PX = 240;

const getDefaultOrdered = (
  images: ResponsiveImageDataWithBadges[],
  exchangeFirstImageOnMobile: boolean
): ResponsiveImageDataWithBadges[] => {
  if (!exchangeFirstImageOnMobile || images.length < 2) return images;
  return [images[1], images[0], ...images.slice(2)].filter(
    (img): img is ResponsiveImageDataWithBadges => img !== undefined
  );
};

const ImageGallery = ({
  images,
  exchangeFirstImageOnMobile = false,
  spacingFactor = 14,
  bottomOffsetDuration = 0,
  imageAltText,
}: ImageGalleryProps) => {
  const t = useTranslations();
  const imageBaseUrl = useImageBaseUrl();
  const [orderedImages, setOrderedImages] = useState(() =>
    getDefaultOrdered(images, exchangeFirstImageOnMobile)
  );
  const [lazyAddImages, setLazyAddImages] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const thumbnailListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setOrderedImages(getDefaultOrdered(images, exchangeFirstImageOnMobile));
    setResetKey(0);
  }, [images, exchangeFirstImageOnMobile]);

  const updateScrollButtons = () => {
    const el = thumbnailListRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [images]);

  if (!orderedImages[0]) return null;

  const handleThumbnailClick = (clicked: ResponsiveImageDataWithBadges) => {
    setOrderedImages([clicked, ...images.filter((img) => img !== clicked)]);
    setLazyAddImages(false);
    setResetKey((k) => k + 1);
    setActiveSlideIndex(0);
  };

  const scrollThumbnails = (direction: 'up' | 'down') => {
    thumbnailListRef.current?.scrollBy({
      top: direction === 'up' ? -THUMB_SCROLL_PX : THUMB_SCROLL_PX,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="@container flex w-full flex-row gap-4"
      style={
        {
          '--image-height': `calc(100svh - var(--header-banner-height) - (var(--header-height) + var(--navigation-second-level-height)) - var(--spacing) * ${spacingFactor})`,
        } as CSSPropertiesWithVars
      }
    >
      <div className="group relative hidden w-36 shrink-0 md:block md:max-h-[min(max(calc((100cqw-10rem)*4/3),850px),var(--image-height))] md:min-h-212.5">
        {canScrollUp && (
          <button
            onClick={() => scrollThumbnails('up')}
            aria-label={t('productCard.a11y.previousThumbnails')}
            className="absolute top-1 left-1/2 z-10 flex size-10 -translate-x-1/2 items-center justify-center rounded-full bg-black p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            <Chevron className="size-6 rotate-180" />
          </button>
        )}

        <ul
          ref={thumbnailListRef}
          className="absolute inset-0 flex flex-col gap-4 overflow-y-auto p-1 no-scrollbar"
          onScroll={updateScrollButtons}
        >
          {images.map((image, index) => (
            <li key={index} className="w-full shrink-0">
              <button
                onClick={() => handleThumbnailClick(image)}
                className={clsx(
                  'block w-full cursor-pointer overflow-hidden rounded-sm',
                  image === orderedImages[activeSlideIndex]
                    ? 'ring-2 ring-black'
                    : 'opacity-60 hover:opacity-100'
                )}
              >
                <GalleryImage
                  imageBaseUrl={imageBaseUrl}
                  image={{ ...image, badges: [], loading: 'lazy', priority: 'low' }}
                  defaultImageWidth={450}
                  defaultImageHeight={600}
                  className={THUMB_IMAGE_CLASSES}
                />
              </button>
            </li>
          ))}
        </ul>

        {canScrollDown && (
          <button
            onClick={() => scrollThumbnails('down')}
            aria-label={t('productCard.a11y.nextThumbnails')}
            className="absolute bottom-1 left-1/2 z-10 flex size-10 -translate-x-1/2 items-center justify-center rounded-full bg-black p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            <Chevron className="size-6" />
          </button>
        )}
      </div>

      <div className="relative w-full">
        <Slider
          key={resetKey}
          isImageRenderOptimized={true}
          infiniteScroll={true}
          className={{
            root: 'w-full [--slides-gap:--spacing(0)] [--slides-to-show:1]',
            slider: 'max-h-(--image-height) md:min-h-212.5',
            navPrev: 'hidden bg-black text-white md:block',
            navNext: 'hidden bg-black text-white md:block',
          }}
          navigation={{
            visible: true,
            prevLabel: t('productCard.a11y.previousImage'),
            nextLabel: t('productCard.a11y.nextImage'),
          }}
          onProgressChange={(progress, pageCount) => {
            const index = Math.round(progress * (pageCount - 1));
            setActiveSlideIndex(index);
            if (progress > 0 && !lazyAddImages) setLazyAddImages(true);
          }}
        >
          {orderedImages.map((image, index) =>
            index !== 0 && !lazyAddImages ? (
              <div key={index} className="aspect-3/4 w-full" />
            ) : (
              <GalleryImage
                key={index}
                imageBaseUrl={imageBaseUrl}
                image={image}
                imageAltText={imageAltText}
                defaultImageWidth={1200}
                defaultImageHeight={1600}
                className={MAIN_IMAGE_CLASSES}
                bottomOffsetDuration={bottomOffsetDuration}
              />
            )
          )}
        </Slider>
        <div
          role="group"
          style={{ transitionDuration: `${bottomOffsetDuration}ms` }}
          className={clsx(
            'absolute right-6 bottom-6 flex min-h-3 min-w-3 flex-row items-center justify-center gap-2 motion-safe:transition-transform motion-safe:ease-out md:hidden',
            bottomOffsetDuration > 0 ? '-translate-y-10' : 'translate-y-0'
          )}
        >
          {new Array(images.length).fill(0).map((_, index) => (
            <div
              key={index}
              className={clsx(
                'shrink-0 rounded-full border-2 border-black transition-all duration-250 ease-in-out',
                index === activeSlideIndex ? 'size-3 bg-white' : 'size-1 bg-black'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageGallery;
